import React, { useEffect, useMemo } from 'react';
import { FiAlertCircle, FiClock } from 'react-icons/fi';
import Button from '../common/Button/Button';
import LoadingText from '../common/LoadingText/LoadingText';
import {
  COURSE_TYPE_ID_TO_LABEL,
  normalizeCourseLabel,
} from '../../constants/courseMappings';
import { applyAvatarFallback, resolveAvatarSrc } from '../../utils/avatarPlaceholder';
import { useI18n } from '../../i18n/language';
import './PendingLessonHoursPrompt.css';

const safeText = (value) => (typeof value === 'string' ? value.trim() : '');

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '--';
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
};

const formatSessionTime = (value, language = 'zh-CN') => {
  const text = safeText(value);
  if (!text) return '--';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const formatDeadline = (value, language = 'zh-CN') => {
  const text = safeText(value);
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

function PendingLessonHoursPrompt({
  open,
  confirmation,
  totalCount = 0,
  busy = false,
  busyAction = '',
  error = '',
  requiresRecharge = false,
  onConfirm,
  onDispute,
  onRecharge,
}) {
  const { language, t, getCourseDirectionDisplayLabel, getCourseTypeLabel } = useI18n();
  const actionRole = safeText(confirmation?.actionRole) === 'mentor' ? 'mentor' : 'student';
  const courseName = useMemo(() => {
    const normalized = normalizeCourseLabel(confirmation?.courseDirectionId);
    return getCourseDirectionDisplayLabel(confirmation?.courseDirectionId, normalized || t('lessonHours.pendingCourse', '课程'));
  }, [confirmation?.courseDirectionId, getCourseDirectionDisplayLabel, t]);

  const courseType = useMemo(() => {
    const courseTypeId = safeText(confirmation?.courseTypeId);
    return getCourseTypeLabel(courseTypeId, COURSE_TYPE_ID_TO_LABEL[courseTypeId] || '');
  }, [confirmation?.courseTypeId, getCourseTypeLabel]);

  const isMentorAction = actionRole === 'mentor';
  const participantLabel = isMentorAction ? t('lessonHours.student', '学生') : t('lessonHours.mentor', '导师');
  const participantName = safeText(confirmation?.participantName || confirmation?.mentorName) || participantLabel;
  const avatarSrc = resolveAvatarSrc({
    src: confirmation?.participantAvatarUrl || confirmation?.mentorAvatarUrl,
    name: participantName,
    seed: confirmation?.threadId || confirmation?.id || participantName,
    size: 192,
  });

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  if (!open || !confirmation) return null;

  const title = isMentorAction ? t('lessonHours.pendingTitleMentor', '学生对本节课时提出了异议') : t('lessonHours.pendingTitleStudent', '请确认本节课实际课时');
  const deadline = formatDeadline(confirmation?.autoConfirmAt, language);
  const subtitle = isMentorAction
    ? ''
    : deadline
      ? t(
        'lessonHours.pendingAutoConfirmSubtitle',
        '请在 {deadline} 前确认或提出异议，逾期将按导师提交的课时自动确认并扣除',
        { deadline }
      )
      : t('lessonHours.pendingSubtitle', '处理完成前，此窗口不会关闭');
  const valueLabel = isMentorAction ? t('lessonHours.originalHours', '导师原提交课时') : t('lessonHours.mentorSubmittedHours', '导师提交课时');
  const disputedHoursText = formatHours(confirmation?.disputedHours);
  const tipText = isMentorAction
    ? t('lessonHours.tipMentor', '确认后将按学生异议结案；提交平台处理后，将进入平台介入状态')
    : t('lessonHours.tipStudent', '若课时不一致，请直接提出异议，导师需要重新处理');
  const secondaryLabel = isMentorAction ? t('lessonHours.submitPlatformReview', '提交平台处理') : t('lessonHours.dispute', '提出异议');
  const primaryLabel = isMentorAction
    ? t('lessonHours.confirmStudentDispute', '确认学生异议')
    : requiresRecharge
      ? t('lessonHours.goTopUp', '去充值')
      : t('lessonHours.confirmHours', '确认课时');
  const secondaryBusy = busy && busyAction === 'secondary';
  const primaryBusy = busy && !secondaryBusy;

  return (
    <div className="pending-lesson-hours-overlay" role="presentation">
      <div
        className="pending-lesson-hours-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('lessonHours.pendingAria', '待确认课时')}
      >
        <div className="pending-lesson-hours-head">
          <h2 className="pending-lesson-hours-title">{title}</h2>
          {subtitle ? (
            <p className="pending-lesson-hours-subtitle">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="pending-lesson-hours-mentor">
          <img
            className="pending-lesson-hours-avatar"
            src={avatarSrc}
            alt=""
            loading="lazy"
            onError={(event) => applyAvatarFallback(event, {
              name: participantName,
              seed: confirmation?.threadId || confirmation?.id || participantName,
              size: 192,
            })}
          />
          <div className="pending-lesson-hours-mentor-copy">
            <div className="pending-lesson-hours-mentor-label">{participantLabel}</div>
            <div className="pending-lesson-hours-mentor-name">{participantName}</div>
          </div>
        </div>

        <div className="pending-lesson-hours-course">
          <div className="pending-lesson-hours-course-name">
            {courseName}
            {courseType ? <span className="pending-lesson-hours-course-type"> · {courseType}</span> : null}
          </div>
          <div className="pending-lesson-hours-course-time">
            <FiClock size={15} aria-hidden="true" />
            <span>{formatSessionTime(confirmation?.startsAt, language)}</span>
          </div>
        </div>

        <div className="pending-lesson-hours-value-card">
          <div className="pending-lesson-hours-value-label">{valueLabel}</div>
            <div className="pending-lesson-hours-value">{formatHours(confirmation?.proposedHours)} {t('lessonHours.hoursUnit', '小时')}</div>
        </div>

        {isMentorAction && confirmation?.disputedHours ? (
          <div className="pending-lesson-hours-value-card pending-lesson-hours-value-card--secondary">
            <div className="pending-lesson-hours-value-label">{t('lessonHours.studentClaimedHours', '学生主张课时')}</div>
            <div className="pending-lesson-hours-value">{disputedHoursText} {t('lessonHours.hoursUnit', '小时')}</div>
          </div>
        ) : null}

        <div className="pending-lesson-hours-tip">
          <FiAlertCircle size={15} aria-hidden="true" />
          <span>{tipText}</span>
        </div>

        {totalCount > 1 ? (
          <div className="pending-lesson-hours-queue">
            {t('lessonHours.queue', `当前还有 ${totalCount} 条待处理课时确认，会按顺序逐条处理`, { count: totalCount })}
          </div>
        ) : null}

        {error ? (
          <div className="pending-lesson-hours-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="pending-lesson-hours-actions">
          <Button
            className="pending-lesson-hours-btn pending-lesson-hours-btn--ghost"
            onClick={() => onDispute?.(confirmation)}
            disabled={busy}
          >
            {secondaryBusy ? <LoadingText text={t('lessonHours.processing', '处理中...')} /> : secondaryLabel}
          </Button>
          <Button
            className="pending-lesson-hours-btn pending-lesson-hours-btn--primary"
            onClick={() => (
              requiresRecharge && !isMentorAction
                ? onRecharge?.(confirmation)
                : onConfirm?.(confirmation)
            )}
            disabled={busy}
          >
            {primaryBusy ? <LoadingText text={t('lessonHours.processing', '处理中...')} /> : primaryLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PendingLessonHoursPrompt;
